import { Vector3, Euler } from 'three';

/**
 * Enum defining the types of training track elements
 */
export enum TrainingTrackElementType {
    CHECKPOINT = 'CHECKPOINT',
    TARGET = 'TARGET',
    RAMP = 'RAMP',
    OBSTACLE = 'OBSTACLE',
    FIRE_RING = 'FIRE_RING',
    BOOSTER = 'BOOSTER',
    POWER_UP = 'POWER_UP',
    TRACK_MARKER = 'TRACK_MARKER'
}

/**
 * Interface for a training track element
 */
export interface TrainingTrackElement {
    id: string;
    type: TrainingTrackElementType;
    position: Vector3;
    rotation: Euler;
    scale: number;
    properties?: TrainingTrackElementProperties;
}

/**
 * Interface for training track element properties
 */
export interface TrainingTrackElementProperties {
    // Common properties
    active?: boolean;
    triggerRadius?: number;
    
    // Checkpoint specific properties
    checkpointNumber?: number;
    timeBonus?: number;
    isStart?: boolean;
    isFinish?: boolean;
    
    // Target specific properties
    targetType?: TargetType;
    health?: number;
    points?: number;
    movementPattern?: MovementPattern;
    movementSpeed?: number;
    isCascading?: boolean;
    isCascadable?: boolean;
    
    // Ramp specific properties
    rampHeight?: number;
    triggerSlowMotion?: boolean;
    
    // Booster specific properties
    boostStrength?: number;
    boostDuration?: number;
    
    // Power-up specific properties
    powerUpType?: PowerUpType;
    duration?: number;
    
    // Fire ring specific properties
    ringRadius?: number;
    fireIntensity?: number;
    
    // Track marker specific properties
    glowing?: boolean;
    
    // Obstacle specific properties
    destructible?: boolean;
    collapsing?: boolean;
}

/**
 * Enum defining the types of targets
 */
export enum TargetType {
    STATIC = 'STATIC',
    MOVING = 'MOVING',
    CASCADING = 'CASCADING'
}

/**
 * Enum defining the types of movement patterns for moving targets
 */
export enum MovementPattern {
    LINEAR = 'LINEAR',
    CIRCULAR = 'CIRCULAR',
    RANDOM = 'RANDOM',
    ZIGZAG = 'ZIGZAG'
}

/**
 * Enum defining the types of power-ups
 */
export enum PowerUpType {
    SPEED_BOOST = 'SPEED_BOOST',
    SHIELD = 'SHIELD',
    RAPID_FIRE = 'RAPID_FIRE',
    HEALTH_BOOST = 'HEALTH_BOOST'
}

/**
 * Interface for a training track
 */
export interface TrainingTrack {
    id: string;
    name: string;
    description: string;
    authorId: string;
    startPosition: Vector3;
    startRotation: number;
    elements: TrainingTrackElement[];
    bestTime?: number;
    bestScore?: number;
    createdAt: string;
    updatedAt: string;
    isPublic: boolean,
    difficulty: string;
    tags: string[];
}

/**
 * Interface for training track progress
 */
export interface TrainingTrackProgress {
    trackId: string;
    playerId: string;
    startTime: number;
    currentTime: number;
    checkpointsReached: number[];
    targetsDestroyed: number;
    score: number;
    completed: boolean;
    bestTime?: number;
    bestScore?: number;
}

/**
 * Interface for training track score
 */
export interface TrainingTrackScore {
    trackId: string;
    playerId: string;
    playerName: string;
    time: number;
    score: number;
    targetsDestroyed: number;
    date: number;
}